import React, { useState, useEffect } from 'react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Plus, Trash2, Calculator } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';

const ApplianceTable = ({ onChange }) => {
  const [appliances, setAppliances] = useState([
    { id: 1, name: '', quantity: 1, unitPower: 0, totalPower: 0, hoursPerDay: 0, energy: 0 }
  ]);

  const [totals, setTotals] = useState({
    totalPower: 0,
    totalEnergy: 0
  });

  // Calculer automatiquement les totaux
  useEffect(() => {
    const newTotals = appliances.reduce((acc, app) => {
      acc.totalPower += app.totalPower || 0;
      acc.totalEnergy += app.energy || 0;
      return acc;
    }, { totalPower: 0, totalEnergy: 0 });

    setTotals(newTotals);
    
    // Envoyer les données au parent
    if (onChange) {
      onChange({ appliances, totals: newTotals });
    }
  }, [appliances, onChange]);

  const handleApplianceChange = (id, field, value) => {
    setAppliances(prev => prev.map(app => {
      if (app.id === id) {
        const updated = { ...app, [field]: value };
        
        // Calculer automatiquement totalPower
        if (field === 'quantity' || field === 'unitPower') {
          const qty = field === 'quantity' ? parseFloat(value) || 0 : parseFloat(app.quantity) || 0;
          const power = field === 'unitPower' ? parseFloat(value) || 0 : parseFloat(app.unitPower) || 0;
          updated.totalPower = qty * power;
        }
        
        // Calculer automatiquement energy
        if (field === 'totalPower' || field === 'hoursPerDay') {
          const tPower = field === 'totalPower' ? parseFloat(value) || 0 : parseFloat(app.totalPower) || 0;
          const hours = field === 'hoursPerDay' ? parseFloat(value) || 0 : parseFloat(app.hoursPerDay) || 0;
          updated.energy = tPower * hours;
        }
        
        // Si on change quantity ou unitPower, recalculer energy aussi
        if (field === 'quantity' || field === 'unitPower') {
          const hours = parseFloat(app.hoursPerDay) || 0;
          updated.energy = updated.totalPower * hours;
        }
        
        return updated;
      }
      return app;
    }));
  };

  const addAppliance = () => {
    const newId = Math.max(...appliances.map(a => a.id), 0) + 1;
    setAppliances(prev => [...prev, {
      id: newId,
      name: '',
      quantity: 1,
      unitPower: 0,
      totalPower: 0,
      hoursPerDay: 0,
      energy: 0
    }]);
  };

  const removeAppliance = (id) => {
    if (appliances.length > 1) {
      setAppliances(prev => prev.filter(app => app.id !== id));
    }
  };

  // Appareils prédéfinis
  const commonAppliances = [
    { name: 'Ampoule LED', power: 10 },
    { name: 'Ventilateur', power: 75 },
    { name: 'Réfrigérateur', power: 150 },
    { name: 'Téléviseur', power: 120 },
    { name: 'Climatiseur', power: 2500 },
    { name: 'Ordinateur', power: 200 },
    { name: 'Four micro-ondes', power: 1200 },
    { name: 'Machine à laver', power: 2000 },
  ];

  const addCommonAppliance = (name, power) => {
    const newId = Math.max(...appliances.map(a => a.id), 0) + 1;
    setAppliances(prev => [...prev, {
      id: newId,
      name: name,
      quantity: 1,
      unitPower: power,
      totalPower: power,
      hoursPerDay: 0,
      energy: 0
    }]);
  };

  return (
    <Card className="border-0 shadow-lg bg-white">
      <CardHeader>
        <CardTitle className="text-xl font-bold text-gray-900 flex items-center">
          <Calculator className="w-5 h-5 mr-2 text-yellow-600" />
          Calculateur de Consommation Énergétique
        </CardTitle>
        <CardDescription>
          Ajoutez vos appareils pour calculer vos besoins énergétiques
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Appareils courants */}
        <div className="mb-4">
          <Label className="text-sm font-medium mb-2 block">Appareils courants :</Label>
          <div className="flex flex-wrap gap-2">
            {commonAppliances.map((app, idx) => (
              <Button
                key={idx}
                type="button"
                variant="outline"
                size="sm"
                onClick={() => addCommonAppliance(app.name, app.power)}
                className="text-xs"
              >
                <Plus className="w-3 h-3 mr-1" />
                {app.name} ({app.power}W)
              </Button>
            ))}
          </div>
        </div>

        {/* Tableau des appareils */}
        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <p className="text-sm text-gray-600">Complétez le tableau ci-dessous</p>
            <p className="text-xs text-gray-500 md:hidden">👉 Faites défiler horizontalement</p>
          </div>
          <div className="overflow-x-auto -mx-2 px-2 border rounded-lg">
            <table className="w-full border-collapse min-w-[700px]">
            <thead>
              <tr className="bg-gray-100">
                <th className="border p-2 text-left text-sm font-semibold">Appareil</th>
                <th className="border p-2 text-center text-sm font-semibold">Qté</th>
                <th className="border p-2 text-center text-sm font-semibold">Puiss. Unit. (W)</th>
                <th className="border p-2 text-center text-sm font-semibold">Puiss. Tot. (W)</th>
                <th className="border p-2 text-center text-sm font-semibold">Temps (h/jour)</th>
                <th className="border p-2 text-center text-sm font-semibold">Énergie (Wh)</th>
                <th className="border p-2 text-center text-sm font-semibold">Action</th>
              </tr>
            </thead>
            <tbody>
              {appliances.map((app) => (
                <tr key={app.id} className="hover:bg-gray-50">
                  <td className="border p-2">
                    <Input
                      type="text"
                      placeholder="Ex: Ampoule LED"
                      value={app.name}
                      onChange={(e) => handleApplianceChange(app.id, 'name', e.target.value)}
                      className="h-9 text-sm"
                    />
                  </td>
                  <td className="border p-2">
                    <Input
                      type="number"
                      min="1"
                      value={app.quantity}
                      onChange={(e) => handleApplianceChange(app.id, 'quantity', e.target.value)}
                      className="h-9 text-center text-sm"
                    />
                  </td>
                  <td className="border p-2">
                    <Input
                      type="number"
                      min="0"
                      placeholder="150"
                      value={app.unitPower || ''}
                      onChange={(e) => handleApplianceChange(app.id, 'unitPower', e.target.value)}
                      className="h-9 text-center text-sm"
                    />
                  </td>
                  <td className="border p-2 bg-gray-50">
                    <div className="text-center font-semibold text-sm">
                      {app.totalPower.toFixed(0)}
                    </div>
                  </td>
                  <td className="border p-2">
                    <Input
                      type="number"
                      min="0"
                      max="24"
                      step="0.5"
                      placeholder="8"
                      value={app.hoursPerDay || ''}
                      onChange={(e) => handleApplianceChange(app.id, 'hoursPerDay', e.target.value)}
                      className="h-9 text-center text-sm"
                    />
                  </td>
                  <td className="border p-2 bg-gray-50">
                    <div className="text-center font-semibold text-sm">
                      {app.energy.toFixed(0)}
                    </div>
                  </td>
                  <td className="border p-2 text-center">
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={() => removeAppliance(app.id)}
                      disabled={appliances.length === 1}
                      className="h-8 w-8 p-0"
                    >
                      <Trash2 className="w-4 h-4 text-red-500" />
                    </Button>
                  </td>
                </tr>
              ))}
              {/* Ligne TOTAL */}
              <tr className="bg-yellow-50 font-bold">
                <td className="border p-2 text-sm">TOTAL</td>
                <td className="border p-2"></td>
                <td className="border p-2"></td>
                <td className="border p-2 text-center text-sm">{totals.totalPower.toFixed(0)} W</td>
                <td className="border p-2"></td>
                <td className="border p-2 text-center text-sm">{totals.totalEnergy.toFixed(0)} Wh</td>
                <td className="border p-2"></td>
              </tr>
            </tbody>
          </table>
          </div>
        </div>

        {/* Bouton ajouter ligne */}
        <Button
          type="button"
          variant="outline"
          onClick={addAppliance}
          className="w-full"
        >
          <Plus className="w-4 h-4 mr-2" />
          Ajouter un appareil
        </Button>

        {/* Résumé */}
        <div className="bg-blue-50 p-4 rounded-lg space-y-2">
          <div className="flex justify-between items-center">
            <span className="font-semibold text-gray-700">Puissance totale nécessaire :</span>
            <span className="text-xl font-bold text-blue-600">{totals.totalPower.toFixed(0)} W</span>
          </div>
          <div className="flex justify-between items-center">
            <span className="font-semibold text-gray-700">Consommation journalière :</span>
            <span className="text-xl font-bold text-blue-600">{totals.totalEnergy.toFixed(0)} Wh</span>
          </div>
          <div className="flex justify-between items-center">
            <span className="font-semibold text-gray-700">Capacité batterie recommandée :</span>
            <span className="text-xl font-bold text-green-600">
              {(totals.totalEnergy * 1.5 / 1000).toFixed(1)} kWh
            </span>
          </div>
          <p className="text-xs text-gray-600 mt-2">
            * Capacité recommandée calculée avec une marge de sécurité de 50%
          </p>
        </div>
      </CardContent>
    </Card>
  );
};

export default ApplianceTable;
